import { useQuery } from "@tanstack/react-query";
import { useCallback, useRef, useState } from "react";

/** How often to poll for version changes (in ms). */
const POLL_INTERVAL = 10 * 60 * 1000; // 10 minutes

interface VersionPayload {
  buildId: string;
}

async function fetchBuildId(url: string): Promise<string | null> {
  try {
    const res = await fetch(url, { cache: "no-store" });
    if (!res.ok) return null;
    const data: VersionPayload = await res.json();
    return data.buildId;
  } catch {
    return null;
  }
}

/**
 * Periodically checks whether the frontend bundle, backend API, or IDP has
 * been updated since the page was loaded.
 *
 * Uses `@tanstack/react-query` with `refetchInterval` for automatic polling.
 *
 * - **Frontend**: compares the baked-in `__APP_BUILD_ID__` against
 *   `/version.json` (written at build time by the Vite plugin).
 * - **Backend API**: polls `GET /api/diagnostics` and compares against the value
 *   returned on the first successful fetch.
 * - **IDP**: polls `GET /id/api/diagnostics` and compares against the value
 *   returned on the first successful fetch.
 *
 * Returns `{ showBanner, dismiss }` so the UI can show and later hide the
 * banner without stopping the underlying poll.
 */
export function useOutdatedCheck() {
  const [isDismissed, setIsDismissed] = useState(false);
  const initialApiBuildId = useRef<string | null>(null);
  const initialIdpBuildId = useRef<string | null>(null);

  const dismiss = useCallback(() => setIsDismissed(true), []);

  const { data: isOutdated = false } = useQuery({
    queryKey: ["outdated-check"],
    queryFn: async () => {
      const [feBuildId, apiBuildId, idpBuildId] = await Promise.all([
        fetchBuildId("/version.json"),
        fetchBuildId("/api/version"),
        fetchBuildId("/id/api/version"),
      ]);

      // Frontend: compare against the build-time constant
      const feOutdated = feBuildId !== null && feBuildId !== __APP_BUILD_ID__;

      // API: capture the initial value, then compare on subsequent polls
      if (apiBuildId !== null && initialApiBuildId.current === null) {
        initialApiBuildId.current = apiBuildId;
      }
      const apiOutdated =
        apiBuildId !== null &&
        initialApiBuildId.current !== null &&
        apiBuildId !== initialApiBuildId.current;

      // IDP: same pattern as API
      if (idpBuildId !== null && initialIdpBuildId.current === null) {
        initialIdpBuildId.current = idpBuildId;
      }
      const idpOutdated =
        idpBuildId !== null &&
        initialIdpBuildId.current !== null &&
        idpBuildId !== initialIdpBuildId.current;

      return feOutdated || apiOutdated || idpOutdated;
    },
    refetchInterval: POLL_INTERVAL,
    refetchIntervalInBackground: true,
    staleTime: POLL_INTERVAL,
    gcTime: Number.POSITIVE_INFINITY,
    refetchOnWindowFocus: false,
    retry: false,
  });

  return {
    showBanner: isOutdated && !isDismissed,
    dismiss,
  };
}
